import classNames from 'classnames'
import { Override } from 'maeng-type/Common'
import { ComponentProps, ReactNode, forwardRef, useMemo } from 'react'
import { isReactNodeHelperText, isReactNodeLabel } from '../../utils/validations'

export type TextInputProps = Override<
  ComponentProps<'input'>,
  {
    label?:
      | ReactNode
      | {
          title: ReactNode
          alt?: ReactNode
        }
    helperText?:
      | ReactNode
      | {
          text: ReactNode
          alt?: ReactNode
        }
    bordered?: boolean
    ghost?: boolean
    color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error'
    size?: 'xs' | 'sm' | 'md' | 'lg'
  }
>

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      helperText,
      bordered = false,
      ghost = false,
      color,
      size = 'md',
      className: classProps = '',
      ...props
    },
    ref,
  ) => {
    const className = useMemo(() => {
      const colors = {
        'input-primary': color === 'primary',
        'input-secondary': color === 'secondary',
        'input-accent': color === 'accent',
        'input-info': color === 'info',
        'input-success': color === 'success',
        'input-warning': color === 'warning',
        'input-error': color === 'error',
      }

      const sizes = {
        'input-xs': size === 'xs',
        'input-sm': size === 'sm',
        'input-md': size === 'md',
        'input-lg': size === 'lg',
      }

      return classNames({
        input: true,
        'focus:outline-offset-0': true,
        'input-bordered': bordered,
        'input-ghost': ghost,
        ...colors,
        ...sizes,
        [classProps]: true,
      })
    }, [bordered, classProps, color, ghost, size])

    const labels = useMemo(() => {
      if (!label) return null
      return (
        <label className="label">
          <span className="label-text">
            {isReactNodeLabel(label) ? label : label.title}
            {!isReactNodeLabel(label) && label.alt && (
              <span className="label-text-alt">{label.alt}</span>
            )}
          </span>
        </label>
      )
    }, [label])

    const helperTexts = useMemo(() => {
      if (!helperText) return null

      return (
        <label className="label">
          <span className="label-text">
            {isReactNodeHelperText(helperText) ? helperText : helperText.text}
            {!isReactNodeHelperText(helperText) && helperText.alt && (
              <span className="label-text-alt">{helperText.alt}</span>
            )}
          </span>
        </label>
      )
    }, [helperText])

    return (
      <div className="form-control">
        {labels}
        <input ref={ref} type="text" className={className} {...props} />
        {helperTexts}
      </div>
    )
  },
)

export default TextInput
